import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "./AuthContext.jsx";
import { useTheme } from "./ThemeContext.jsx";
import apiUserSettings from "../api/global-api/api-settings.js";

// Single source of truth for the logged-in user's settings record (theme,
// timezone, currency, working hours, notification prefs, privacy toggles...).
// Fetched once during app bootstrap — alongside AuthContext's profile/modules
// fetch — so the whole app (Header, Settings page, future features) shares
// one copy instead of every consumer re-fetching it, and so the correct theme
// is applied before the app shell ever paints (no light-mode flash on refresh).
export const SettingsContext = createContext();

export default function SettingsProvider({ children }) {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { setTheme } = useTheme();

  const [settings, setSettings] = useState(null);
  const [settingsId, setSettingsId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Settings request started on mount, in parallel with AuthContext's
  // profile/modules requests. Used only once auth resolves to a user.
  const bootstrapRequestRef = useRef(null);

  const loadSettings = useCallback(async (request) => {
    try {
      const res = await request;
      const data = res?.data?.data;
      setSettings(data || null);
      setSettingsId(data?.id ?? null);
      if (data?.theme) {
        setTheme(data.theme.toLowerCase());
      }
      return data;
    } catch (err) {
      console.log("Load user settings error:", err);
      return null;
    }
  }, [setTheme]);

  const fetchSettings = useCallback(
    () => loadSettings(apiUserSettings.getMySettings()),
    [loadSettings]
  );

  useEffect(() => {
    const request = apiUserSettings.getMySettings();
    request.catch(() => {}); // logged out: rejection is expected and ignored
    bootstrapRequestRef.current = request;
  }, []);

  useEffect(() => {
    // Wait for auth to resolve — settings are per-user and the endpoint needs
    // an authenticated session.
    if (authLoading) return;

    const bootstrapRequest = bootstrapRequestRef.current;
    bootstrapRequestRef.current = null;

    if (!user) {
      // Logged out (or never logged in): drop any stale record so a
      // different user logging in on the same tab doesn't briefly see it.
      setSettings(null);
      setSettingsId(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    (bootstrapRequest ? loadSettings(bootstrapRequest) : fetchSettings())
      .finally(() => setLoading(false));
  }, [authLoading, user, fetchSettings, loadSettings]);

  // Merge a local patch immediately after a save (Header's quick theme
  // toggle, Settings page save) so every consumer reflects it without
  // waiting on a full refetch.
  const updateSettingsLocal = useCallback((partial) => {
    setSettings((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const value = useMemo(
    () => ({ settings, settingsId, loading, refreshSettings: fetchSettings, updateSettingsLocal }),
    [settings, settingsId, loading, fetchSettings, updateSettingsLocal]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

// Convenience hook: const { settings, settingsId, loading } = useSettings();
export const useSettings = () => useContext(SettingsContext);
